import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Lesson } from '../../data/models/Lesson';
import { LessonPage } from '../../data/models/LessonPage';
import { LessonService } from '../../networking/services/lesson.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LessonPageItemService } from '../../networking/services/lesson-page-item.service';
import { LessonPageItem } from '../../data/models/LessonPageItem';

import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LessonPictureService } from '../../networking/services/lesson-picture.service';
import { LessonPicture } from '../../data/models/LessonPicture';
import { MatRadioModule } from '@angular/material/radio';
import { FilterVideoTypePipe } from '../../helper/pipes/filter-video-type.pipe';
import { DialogService } from '../../helper/services/dialog.service';
import { concatMap, firstValueFrom, forkJoin, from } from 'rxjs';
import { QuizAnswerService } from '../../networking/services/quiz-answer.service';
import { QuizAnswer } from '../../data/models/QuizAnswer';
import { HelperService } from '../../helper/services/helper.service';
import { FilterTextTypePipe } from "../../helper/pipes/filter-text-type.pipe";
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { LessonPageItemType } from '../../data/models/LessonPageItemType';
import { LessonPageItemTypeService } from '../../networking/services/lesson-page-item-type.service';
import { LessonPageItemTypeEnum } from '../../data/enums/LessonPageItemType';

type QuizAnswerDraft = Partial<QuizAnswer> & {
  answer_text: string;
  response_text: string;
};

@Component({
    selector: 'app-lesson-edit',
    imports: [MatIconModule, FormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatRadioModule, FilterVideoTypePipe, FilterTextTypePipe, MatSlideToggleModule],
    templateUrl: './lesson-edit.component.html',
    styleUrl: './lesson-edit.component.scss'
})
export class LessonEditComponent implements OnInit {
  lessonPages: any = [];
  lesson!: Lesson;
  lessonPageItems: LessonPageItem[] = [];
  lessonTitle!: string
  selectedImageId: string | undefined = undefined;
  lessonPictures: LessonPicture[] = []
  lessonPageItemTypes: LessonPageItemType[] = []

  quizQuestion!: string
  quizAnswers: QuizAnswerDraft[] = [];
  existingQuizId?: string;

  currentPageIndex = 0;
  originalLessonTitle: string = '';
  originalImageId: string = '';
  picturePath: string = '';

  constructor(private lessonService: LessonService, private activatedRoute: ActivatedRoute, private lessonPageItemService: LessonPageItemService, private router: Router, private lessonPictureService: LessonPictureService, private cdr: ChangeDetectorRef, private dialogService: DialogService, private quizAnswerService: QuizAnswerService, private helperService: HelperService, private lessonPageItemTypeService: LessonPageItemTypeService) { }

  ngOnInit() {
    this.loadLessonData();
  }

  async loadLessonData() {
    const lessonId = this.activatedRoute.snapshot.paramMap.get('id');
    if (lessonId) {
      this.lessonService.getLessonDetails(lessonId).subscribe((lesson) => {
        this.lesson = lesson;
        if(lesson.lesson_pages){
          this.lessonPages = (lesson.lesson_pages as unknown as any[]).sort((a, b) => a.order - b.order);
        }
        this.lessonTitle = lesson.title;
        this.selectedImageId = lesson.lesson_picture_id;

        this.originalLessonTitle = this.lesson.title;
        this.originalImageId = this.lesson.lesson_picture_id;

        if (this.currentPageIndex >= 0 && this.currentPageIndex < this.lessonPages.length) {
          this.goToPage(this.currentPageIndex);
        }
        this.cdr.detectChanges();
      });
      this.lessonPageItemTypeService.getLessonPageItemTypeList().subscribe((types) => {
        this.lessonPageItemTypes = types;
      });
    }

    this.lessonPictureService.getLessonPictureList().subscribe({
      next: (pictures) => {
        this.lessonPictures = pictures;
      },
      error: (error) => {
        console.error('Error loading lesson pictures:', error);
      }
    });
  }

  metaDataHasChanges(): boolean {
    return (
      this.lessonTitle !== this.originalLessonTitle ||
      this.selectedImageId !== this.originalImageId
    );
  }

  loadPageItems(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.lessonPageItemService.getLessonPageItemList(id).subscribe({
        next: (items) => {
          this.lessonPageItems = items;
          resolve();
        },
        error: (error) => {
          console.error('Failed to load page items:', error);
          reject(error);
        }
      });
    });
  }

  goToPage(index: number) {
    if (index === 0) {
      this.currentPageIndex = 0;
      this.lessonPageItems = [];
      this.quizAnswers = [];
      this.quizQuestion = '';
      this.existingQuizId = undefined;
      this.cdr.detectChanges();
    } else if (index > 0 && index <= this.lessonPages.length) {
      if (this.currentPageIndex === 4 && index !== 4) {
        this.existingQuizId = undefined;
      }
      this.currentPageIndex = index;
      const adjustedIndex = index - 1;

      this.lessonPageItems = [];
      this.loadPageItems(this.lessonPages[adjustedIndex].id).then(() => {

        if (index === 4) {
          if (this.lessonPageItems.length > 0) {
            this.quizQuestion = this.lessonPageItems[0].content;
            this.quizAnswers = this.lessonPageItems[0].quiz_answers.map(a => ({
              ...a,
            }));
            this.existingQuizId = this.lessonPageItems[0].id;
          } else {
            this.resetQuizState();
          }
        }

        this.cdr.detectChanges();
      });
    }
    this.loadPicture()
  }

  private reloadCurrentPage() {
    if (this.currentPageIndex === 0) {
      this.loadLessonData();
    } else {
      const adjustedIndex = this.currentPageIndex - 1;
      if (adjustedIndex >= 0 && adjustedIndex < this.lessonPages.length) {
        this.loadPageItems(this.lessonPages[adjustedIndex].id).then(() => {
          if (this.currentPageIndex === 4 && this.lessonPageItems.length > 0) {
            this.quizQuestion = this.lessonPageItems[0].content;
            this.quizAnswers = this.lessonPageItems[0].quiz_answers;
          }

          this.cdr.detectChanges();
        });
      }
    }
  }

  saveLesson() {
    this.lessonService.alterLesson(
      this.lesson.id,
      {
        title: this.lessonTitle,
        lessonPictureId: this.selectedImageId
      }).subscribe({
        next: () => {
          this.reloadCurrentPage()
        }
      })
  }

  goToDashboard() {
    this.router.navigate(['course-manager/dashboard']);
  }

  async addMaterial() {
    const result = await this.dialogService.openUploadDialog()
    if (result) {
      const { materialName, file } = result;
      const lessonPageId = (this.lessonPages[this.currentPageIndex - 1].id);
      const videoType = this.lessonPageItemTypes.find(
        (type) => type.title === LessonPageItemTypeEnum.VIDEO
      );

      if (!videoType) {
        console.error('LessonPageItemType "Video" not found');
        return;
      }
      this.lessonPageItemService
        .uploadLessonPageItem(file, lessonPageId, videoType.id, materialName)
        .subscribe({
          next: () => {
            this.reloadCurrentPage();
          },
          error: (error) => {
            console.error('Upload failed:', error);
          }
        });
    }

  }

  async deleteItem(id: string) {
    const confirmed = await this.dialogService.openConfirmDialog(
      'Lektionselement löschen?',
      'Möchtest du dieses Lektionselement wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
    );

    if (confirmed) {
      const isQuizItem = this.existingQuizId === id;

      this.lessonPageItemService.deleteLessonPageItem(id).subscribe({
        next: () => {
          if (isQuizItem) {
            this.resetQuizState();
          }
          this.reloadCurrentPage();
        },
        error: (error) => {
          console.error('Failed to delete item:', error);
        }
      });
    }
  }

  async saveQuiz() {
    const lessonPage = this.lessonPages[this.currentPageIndex - 1];

    if (this.quizAnswers.some(a => !a.answer_text || !a.response_text)) {
      alert('Alle Antwort- und Auflösungstexte müssen ausgefüllt sein.');
      return;
    }

    const isNewQuiz = this.lessonPageItems.length === 0;

    try {
      let quizItemId: string;
      const quizType = this.lessonPageItemTypes.find(
        (type) => type.title === LessonPageItemTypeEnum.QUIZ
      );

      if (!quizType) {
        console.error('LessonPageItemType "Quiz" not found');
        return;
      }

      if (isNewQuiz) {
        const created = await firstValueFrom(this.lessonPageItemService.createLessonPageItem({
          content: this.quizQuestion,
          lesson_page_id: lessonPage.id,
          lessonPageItemTypeId: quizType.id
        }));

        quizItemId = created.id;

        const createCalls = this.quizAnswers.map(answer =>
          this.quizAnswerService.createQuizAnswer({
            lessonPageItemId: quizItemId,
            answerText: answer.answer_text,
            responseText: answer.response_text,
            isCorrect: true
          })
        );

        await Promise.all(createCalls.map(firstValueFrom));
        this.lessonPageItems = [created];
        this.existingQuizId = created.id;

      } else {
        quizItemId = this.lessonPageItems[0].id;

        await firstValueFrom(this.lessonPageItemService.alterLessonPageItem(quizItemId, {
          content: this.quizQuestion
        }));

        const updateCalls = this.quizAnswers.map(answer => {
          if (answer.id) {
            return this.quizAnswerService.alterQuizAnswer(answer.id, {
              answerText: answer.answer_text,
              responseText: answer.response_text
            });
          } else {
            return this.quizAnswerService.createQuizAnswer({
              lessonPageItemId: quizItemId,
              answerText: answer.answer_text,
              responseText: answer.response_text,
              isCorrect: true
            });
          }
        });

        await Promise.all(updateCalls.map(firstValueFrom));
      }

      this.reloadCurrentPage();

    } catch (error) {
      console.error('Fehler beim Speichern des Quiz:', error);
    }
  }

  async addText() {
    const dialogResult = await this.dialogService.openTextContentDialog();

    if (!dialogResult) {
      return;
    }

    const textType = this.lessonPageItemTypes.find(
      (type) => type.title === LessonPageItemTypeEnum.TEXT
    );

    if (!textType) {
      console.error('LessonPageItemType "Text" not found');
      return;
    }

    this.lessonPageItemService.createLessonPageItem({
      content: dialogResult.content,
      lesson_page_id: this.lessonPages[this.currentPageIndex - 1].id,
      lessonPageItemTypeId: textType.id,
      title: dialogResult.title
    }).subscribe({
      next: () => {
        this.reloadCurrentPage();
      }
    });
  }

  addAnswer() {
    if (this.quizAnswers.length < 5) {
      this.quizAnswers.push({
        answer_text: '',
        response_text: ''
      });
    }
  }

  async deleteAnswer(index: number) {
    const answer = this.quizAnswers[index];

    if (index < 3) return;

    const confirmed = await this.dialogService.openConfirmDialog(
      'Quiz Antwort löschen?',
      'Möchtest du diese Quiz Antwort wirklich löschen?'
    );

    if (!confirmed) return;

    if (answer.id) {
      this.quizAnswerService.deleteQuizAnswer(answer.id).subscribe({
        next: () => {
          this.quizAnswers.splice(index, 1);
        },
        error: (error) => {
          console.error('Error while deleting:', error);
        }
      });
    } else {
      this.quizAnswers.splice(index, 1);
    }
  }

  saveItem(item: LessonPageItem) {
    this.lessonPageItemService.alterLessonPageItem(item.id, {
      title: item.title ?? undefined,
      content: item.content ?? undefined
    }).subscribe({
      next: () => {
        this.reloadCurrentPage()
      }
    });
  }
  async deleteLesson() {
    const confirmed = await this.dialogService.openConfirmDialog(
      'Lektion löschen?',
      'Möchtest du diese Lektion wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
    );

    if (confirmed) {
      this.lessonService.deleteLesson(this.lesson.id).subscribe({
        next: () => {
          this.goToDashboard()
        }
      });
    }
  }

  private resetQuizState() {
    this.existingQuizId = undefined;
    this.quizQuestion = '';
    this.quizAnswers = Array(3).fill(null).map(() => ({
      answer_text: '',
      response_text: '',
      isCorrect: false
    }));
  }

  togglePublicationStatus(): void {
    if (!this.lesson?.id) return;

    this.lessonService.toggleLessonPublication(this.lesson.id).subscribe({
      next: (updatedLesson) => {
        this.lesson = updatedLesson;
      },
      error: (err) => {
        console.error('Could not toggle publication status:', err);
      }
    });
  }

  finishLesson(): void {
    if (!this.lesson.published) {
      this.togglePublicationStatus()
      this.goToDashboard()
    } else {
      this.goToDashboard()
    }
  }

  loadPicture() {
    switch (this.currentPageIndex) {
      case 0:
        this.picturePath = 'assets/images/Baerchen_springt.png';
        break;
      case 1:
        this.picturePath = 'assets/images/Einleitung_Baerchen.png';
        break;
      case 2:
        this.picturePath = 'assets/images/Kursmaterial_Baerchen.png';
        break;
      case 3:
        this.picturePath = 'assets/images/Ueberleitung_Lernaufgabe_Baerchen.png';
        break;
      case 4:
        this.picturePath = 'assets/images/Quiz_Edit_Baerchen.png';
        break;
      case 5:
        this.picturePath = 'assets/images/Fazit_Baerchen.png';
        break;
      case 6:
        this.picturePath = 'assets/images/Abschluss_Baerchen.png';
        break;
      default:
        this.picturePath = 'assets/images/Baerchen_springt.png';
        break;
    }
  }
}
